import { createStore } from "@tellescope/store";
import type { ShopProduct } from "./types";
import { isValidCartItem, filterValidCartItems } from "./shopVariants";

const MAX_QUANTITY = 1;

export interface CartItem {
  /** Stripe product ID (ShopProduct.stripeProductId) — shared by every price variant of this product. */
  stripeProductId: string;
  /**
   * The currently-selected price variant for this product (ShopPriceVariant.stripePriceId).
   * Resolved to a Tellescope Product _id via reverse-lookup immediately before calling
   * prepare_stripe_checkout, not stored here.
   */
  stripePriceId: string;
  // Capped at MAX_QUANTITY for now — buying multiples of the same prescription isn't a real use
  // case, but the field is kept (rather than removed) in case a future non-prescription product
  // type needs it.
  quantity: number;
}

export interface CheckoutProgress {
  medIndex: number;
  questionIndex: number;
  answers: Record<string, string>;
  intakeStarted: boolean;
  intakeComplete: boolean;
  bannerDismissed: boolean;
}

const INITIAL_CHECKOUT_PROGRESS: CheckoutProgress = {
  medIndex: 0,
  questionIndex: 0,
  answers: {},
  intakeStarted: false,
  intakeComplete: false,
  bannerDismissed: false,
};

interface CartState {
  items: CartItem[];
  checkoutProgress: CheckoutProgress;

  /** Adds a product to the cart at the given (typically default/cheapest) variant. No-op if already present. */
  addToCart: (stripeProductId: string, stripePriceId: string) => void;
  removeFromCart: (stripeProductId: string) => void;
  /**
   * Removes any cart item whose product/variant no longer exists in the given products list — e.g.
   * an admin removed it from the Shop block's config after it was added to the cart. Returns the
   * count actually removed, so a caller can decide whether to show a one-time notice; a no-op
   * (returns 0, no state update) when everything is still valid.
   */
  pruneInvalidItems: (products: ShopProduct[]) => number;
  updateQuantity: (stripeProductId: string, quantity: number) => void;
  /** Changes which price variant is selected for a product already in the cart. */
  setCartItemVariant: (stripeProductId: string, stripePriceId: string) => void;
  clearCart: () => void;

  enterCheckout: () => void;
  setCheckoutMedIndex: (medIndex: number) => void;
  setCheckoutQuestionIndex: (questionIndex: number) => void;
  setCheckoutAnswers: (answers: Record<string, string>) => void;
  completeIntake: () => void;
  dismissResumeBanner: () => void;
  resetCheckoutProgress: () => void;
}

export const { useStore: useCartStore } = createStore<CartState>(
  (set, get) => ({
    items: [],
    checkoutProgress: INITIAL_CHECKOUT_PROGRESS,

    addToCart: (stripeProductId, stripePriceId) =>
      set((state) => {
        if (state.items.some((item) => item.stripeProductId === stripeProductId)) return state;
        return { items: [...state.items, { stripeProductId, stripePriceId, quantity: 1 }] };
      }),

    removeFromCart: (stripeProductId) =>
      set((state) => ({ items: state.items.filter((item) => item.stripeProductId !== stripeProductId) })),

    pruneInvalidItems: (products) => {
      const current = get().items;
      const valid = filterValidCartItems(current, products);
      if (valid.length === current.length) return 0;
      set({ items: valid });
      return current.length - valid.length;
    },

    updateQuantity: (stripeProductId, quantity) =>
      set((state) => {
        if (quantity <= 0) {
          return { items: state.items.filter((item) => item.stripeProductId !== stripeProductId) };
        }
        const clamped = Math.min(quantity, MAX_QUANTITY);
        return {
          items: state.items.map((item) =>
            item.stripeProductId === stripeProductId ? { ...item, quantity: clamped } : item
          ),
        };
      }),

    setCartItemVariant: (stripeProductId, stripePriceId) =>
      set((state) => ({
        items: state.items.map((item) =>
          item.stripeProductId === stripeProductId ? { ...item, stripePriceId } : item
        ),
      })),

    clearCart: () => set({ items: [] }),

    enterCheckout: () =>
      set((state) => ({
        checkoutProgress: {
          ...state.checkoutProgress,
          intakeStarted: true,
          intakeComplete: false,
          bannerDismissed: false,
        },
      })),

    setCheckoutMedIndex: (medIndex) => set((state) => ({ checkoutProgress: { ...state.checkoutProgress, medIndex } })),

    setCheckoutQuestionIndex: (questionIndex) =>
      set((state) => ({ checkoutProgress: { ...state.checkoutProgress, questionIndex } })),

    setCheckoutAnswers: (answers) => set((state) => ({ checkoutProgress: { ...state.checkoutProgress, answers } })),

    completeIntake: () =>
      set((state) => ({
        checkoutProgress: {
          ...state.checkoutProgress,
          intakeStarted: false,
          intakeComplete: true,
          bannerDismissed: false,
        },
      })),

    dismissResumeBanner: () =>
      set((state) => ({ checkoutProgress: { ...state.checkoutProgress, bannerDismissed: true } })),

    resetCheckoutProgress: () => set({ checkoutProgress: INITIAL_CHECKOUT_PROGRESS }),
  }),
  {
    persist: "localStorage",
    name: "tellescope-portal-cart-store",
  }
);

export const useCartItems = () => useCartStore((state) => state.items);
/**
 * Counts only cart items whose product/variant still exist in the given products list — an item the
 * admin has since removed from the Shop block's configuration doesn't count, so the badge always
 * matches what CartPanel actually displays. `products` isn't store state (it's the current Shop
 * block's config, owned by the consuming app), so it's a required argument rather than baked into
 * the selector.
 */
export const useCartItemCount = (products: ShopProduct[]) =>
  useCartStore((state) =>
    state.items.reduce((sum, item) => (isValidCartItem(item, products) ? sum + item.quantity : sum), 0)
  );
export const useCheckoutProgress = () => useCartStore((state) => state.checkoutProgress);
